import SafeImage from 'components/SafeImage';
import Copy from './Copy';
import { useTranslate } from '@tolgee/react';
import { Button, Divider, message, Tooltip } from 'antd';
import { DownOutlined, InfoCircleOutlined, UpOutlined } from '@ant-design/icons';
import { useRef, useState } from 'react';
import Fold from './Fold';
import { getErpSkus } from './ToDoBundle/api';
import styled from '@emotion/styled';
import LoadMore from 'components/LoadMore';

function ErpProduct({ erpSku }: { erpSku: Todo.ErpItemSku }) {
  const { skuId, skuName, imgUrl, inventories, subSkus = [] } = erpSku;

  const { t } = useTranslate();

  const [fold, setFold] = useState(true);

  const main = (
    <Item>
      <SafeImage src={imgUrl} width={48} height={48} />
      <section>
        <p>
          <div>
            {subSkus.length > 0 && (
              <span>{t('scene.chatWorkspace.taskTab.erpOrderForm.unit.group')}</span>
            )}
            {skuName}
          </div>
          <Copy
            text={skuName}
            successMessage={t('common.feedback.copySuccess')}
            failedMessage={t('common.feedback.copyFailed')}
          />
        </p>
        <div>
          <p>
            {t('商品编码')}：{skuId}
            <Divider type="vertical" />
            {inventories.some((_) => _.inventory > 0) ? (
              <span>
                {t('stock.exist')}
                <Tooltip
                  title={
                    <div>
                      {inventories.map(({ inventory, warehouseName }) => {
                        return <div>{`${warehouseName}: ${inventory}`}</div>;
                      })}
                    </div>
                  }
                >
                  <InfoCircleOutlined />
                </Tooltip>
              </span>
            ) : (
              t('无货')
            )}
          </p>
          {subSkus.length > 0 && (
            <Button type="text" onClick={() => setFold(!fold)}>
              {t('子商品信息')}
              {fold ? <DownOutlined /> : <UpOutlined />}
            </Button>
          )}
        </div>
      </section>
    </Item>
  );

  if (!subSkus.length) {
    return main;
  }

  const subList = subSkus.map((sku) => {
    const { skuId, skuName, imgUrl, inventories } = sku;

    return (
      <Item>
        <SafeImage src={imgUrl} width={48} height={48} />
        <section>
          <p>
            <div>{skuName}</div>
            <Copy
              text={skuName}
              successMessage={t('common.feedback.copySuccess')}
              failedMessage={t('common.feedback.copyFailed')}
            />
          </p>
          <div>
            <p>
              {t('商品编码')}：{skuId}
              <Divider type="vertical" />
              {t('组合品数量')}：{subSkus.length}
              <Divider type="vertical" />
              {inventories.some((_) => _.inventory > 0) ? (
                <span>
                  {t('stock.exist')}
                  <Tooltip
                    title={
                      <div>
                        {inventories.map(({ inventory, warehouseName }) => {
                          return <div>{`${warehouseName}: ${inventory}`}</div>;
                        })}
                      </div>
                    }
                  >
                    <InfoCircleOutlined />
                  </Tooltip>
                </span>
              ) : (
                t('无货')
              )}
            </p>
          </div>
        </section>
      </Item>
    );
  });

  return (
    <Fold isfold={fold} fixedCount={1} nodeList={[main, <SubItemList>{subList}</SubItemList>]} />
  );
}

function ErpProductList({
  storeId,
  type,
  keyword,
}: {
  storeId: string | number;
  type: 'ERP_SKU_CODE' | 'ERP_SKU_NAME';
  keyword: string;
}) {
  const { t } = useTranslate();

  const pageRef = useRef(1);
  const [hasMore, setHasMore] = useState(true);
  const [itemList, setItemList] = useState<Todo.ErpItemSku[]>([]);

  const fetch = () => {
    return getErpSkus({
      searchType: 'ERP_ITEM',
      storeId,
      erpSearchType: type === 'ERP_SKU_CODE' ? 'SKU_CODE' : 'SKU_NAME',
      keyword,
      page: pageRef.current,
      pageSize: 5,
    })
      .then((res) => {
        pageRef.current += 1;
        if (!res.length) {
          setHasMore(false);
        }
        setItemList([...itemList, ...res]);
      })
      .catch((err) => {
        message.error(t('common.feedback.fetchFailed'));
        return Promise.reject(err);
      });
  };

  return (
    <Container>
      <LoadMore hasMore={hasMore} onLoadMore={fetch}>
        {(loadEle) => {
          return (
            <>
              {itemList.map((sku) => (
                <ErpProduct erpSku={sku} />
              ))}
              {loadEle}
            </>
          );
        }}
      </LoadMore>
    </Container>
  );
}

const Container = styled.div`
  height: 100%;
  overflow: auto;
  > * {
    margin-bottom: 16px;
  }
`;

const Item = styled.div`
  display: flex;
  padding: 10px 8px;
  background: #fff;
  > div {
    flex: 0 0 auto;
    margin-right: 10px;
  }
  > section {
    flex: 1 0 0;
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    overflow: hidden;
    > p {
      display: flex;
      align-items: center;
      margin-bottom: 0;
      > div {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        > span {
          display: inline-block;
          line-height: 20px;
          margin-right: 4px;
          padding: 0 4px;
          color: #fff;
          background: var(--ant-primary-color);
          border-radius: 2px;
        }
      }
      > span {
        flex: 0 0 auto;
      }
    }
    > div {
      display: flex;
      justify-content: space-between;
      align-items: center;
      > p {
        flex: 1 0 0;
        margin-bottom: 0;
        font-size: 12px;
        color: #84868c;
        .anticon {
          margin-left: 4px;
          cursor: pointer;
        }
      }
      > button {
        font-size: 12px;
      }
    }
  }
`;

const SubItemList = styled.div`
  max-height: 300px;
  padding: 0 8px;
  background: #fff;
  overflow: auto;
  > * {
    margin-bottom: 10px;
    background: #f3f5f7 !important;
  }
`;

export default ErpProductList;
